import type { Branding } from '@sunucupark/shared';
import { RouterProvider } from '@tanstack/react-router';
import { inboxFolderIdOf } from './app/inbox';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { paintAppearance, watchAppearance } from './app/appearance';
import { applyBrandChrome, applyBrandRoles, loadBranding } from './app/branding';
import { createWebClient } from './app/client';
import { config } from './app/config';
import { initI18n, preferredLanguage, storedLanguage } from './app/i18n';
import { Providers } from './app/providers';
import { createAppRouter } from './app/routes';
import { usePreferences } from './app/state/preferences';
import { applyColorScheme } from './app/theme';
import { domainOf, OFFLINE_VERSION } from './app/use-branding';
import { loadContracts } from './shared/contracts';
import './styles/index.css';

/**
 * Bootstrap: theme and language before the first paint, then the session the
 * refresh cookie may still prove (ADR 0007), then the brand the gateway serves
 * for it. Only afterwards does the router decide sign-in or shell, so a reload
 * never flashes the sign-in page at a signed-in user — nor our colours at a
 * customer whose domain has its own.
 */
async function start(): Promise<void> {
  const preferences = usePreferences.getState();
  const scheme = preferences.colorScheme;
  applyColorScheme(scheme);
  /*
   * Before the first paint, and over the brand: what a person chose about the
   * look is theirs, and a page that flashed the brand's colours first would
   * be admitting it forgot (ADR 0009 §8). Awaited on purpose — it returns at
   * once unless something was actually changed, in which case the 3 KB of
   * tables are worth the wait.
   */
  await paintAppearance(preferences.appearance, scheme);
  watchAppearance(() => {
    const current = usePreferences.getState();
    return { appearance: current.appearance, colorScheme: current.colorScheme };
  });
  const language = preferredLanguage(storedLanguage() ?? preferences.language, navigator.languages);
  const i18n = initI18n(language);
  document.documentElement.lang = language;

  const client = createWebClient({
    locale: () => i18n.language,
    isMac: /mac/i.test(navigator.platform || navigator.userAgent),
    title: config.brand.name,
    iconUrl: config.brandAssets.favicon,
    // Red, not the brand: the count has to stand out on the brand's own mark.
    badgeColor: '#D93F4C',
  });

  client.shortcuts.setEnabled(preferences.shortcutsEnabled);

  /*
   * The contracts before anything is validated: loading them throws zod's
   * jitless switch, and a schema created before it compiles its validator with
   * `new Function`, which the page's CSP refuses. The session's own answer is
   * the first thing validated now, so they load here rather than with the
   * brand — they always arrived before the first render, so this costs nothing.
   */
  await loadContracts();
  /*
   * The session first: who is signed in decides the brand. Asked the other way
   * round, a signed-in customer was painted in the host's brand on every
   * reload and repainted in their own only when the access token was next
   * renewed — minutes later, often as they came back to the tab.
   */
  await client.session.restore().catch(() => null);
  const branding = await loadBranding(client.api, window.location.host, domainOf(client.session.snapshot()?.email)).catch(
    () => null,
  );
  if (branding) {
    const resolved =
      scheme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : scheme;
    applyBrandRoles(branding, resolved);
    applyBrandChrome(branding);
    // The brand now paints the greys too, so the personal layer goes on again
    // over it: what someone chose for themselves outranks what the brand says
    // the page is made of (ADR 0009 §8). It returns at once unless something
    // was actually chosen.
    await paintAppearance(preferences.appearance, scheme);
  }

  const router = createAppRouter({ client, inboxFolderId: () => inboxFolderIdOf(client) });
  const container = document.getElementById('root');
  if (!container) throw new Error('The page has no #root element');
  createRoot(container).render(
    <StrictMode>
      <Providers client={client} branding={branding ?? fallbackBranding()}>
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>,
  );

  measureThisPageView();
}

/**
 * Real-user measurements (GAP H1). Loaded after the mailbox is on screen and
 * only for the sampled share of page views, so measuring a page never slows
 * it down; the observers read the entries the browser buffered before they
 * started, so nothing is lost by starting late.
 */
function measureThisPageView(): void {
  if (config.vitalsSampleRate <= 0) return;
  const begin = () => {
    void import('./platform/vitals').then((vitals) =>
      vitals.startVitals({ sampleRate: config.vitalsSampleRate, gatewayUrl: config.gatewayUrl }),
    );
  };
  // Declared in the DOM types but missing from older Safari, so it is tested for, not assumed.
  const hasIdleCallback = typeof window.requestIdleCallback === 'function';
  if (hasIdleCallback) window.requestIdleCallback(() => begin(), { timeout: IDLE_TIMEOUT_MS });
  else window.setTimeout(() => begin(), IDLE_TIMEOUT_MS);
}

/** How long to wait for an idle moment before measuring anyway. */
const IDLE_TIMEOUT_MS = 3000;

/**
 * Used only when the gateway cannot be reached at startup: the app still shows
 * a sign-in page (with the packaged brand) instead of a blank screen, and the
 * real brand arrives on the next load.
 */
function fallbackBranding(): Branding {
  const brand = config.brand;
  return {
    id: brand.id,
    version: OFFLINE_VERSION,
    name: brand.name,
    tagline: brand.tagline,
    colors: brand.colors,
    roles: config.brandRoles,
    assets: config.brandAssets,
  };
}

void start();
